<script setup lang="ts">
import type { StandInActionDto, StandInActionKey, StandInSide } from '@swipe4work/models-orval'
import { getGetAdminStandInPairQueryKey, useGetAdminStandInPair, usePerformAdminStandInAction } from '@swipe4work/api-orval'
import { useQueryClient } from '@tanstack/vue-query'
import StandInActionCard from '@/features/tools/components/stand-in/StandInActionCard.vue'
import StandInCandidatePicker from '@/features/tools/components/stand-in/StandInCandidatePicker.vue'
import StandInJobPicker from '@/features/tools/components/stand-in/StandInJobPicker.vue'
import StandInPairState from '@/features/tools/components/stand-in/StandInPairState.vue'
import { useToast } from '@/shared/composables/UseToast'
import { useCompanyStore } from '@/shared/stores/CompanyStore'
import { useViteEnvironment } from '@/shared/composables/useViteEnvironment'

definePage({
  path: '/tools/StandIn',
  name: '/tools/StandIn',
  meta: {
    title: 'Stand-in acties',
    permission: {
      resource: 'Candidates',
      level: 'Read',
    },
  },
})

/*
 * On this page an admin performs real product actions on behalf of a candidate or an employer
 * (Stand-in Actions) for one (candidate × job) pair, to review the other side in the app or portal.
 */
const route = useRoute()
const router = useRouter()
const toast = useToast()
const queryClient = useQueryClient()
const { isProd } = useViteEnvironment()

function parseId(value: unknown): number | null {
  const id = Number(Array.isArray(value) ? value[0] : value)

  return Number.isSafeInteger(id) && id > 0 ? id : null
}

const userId = ref(parseId(route.query.userId))
const jobId = ref(parseId(route.query.jobId))

watch([ userId, jobId ], ([ user, job ]) => {
  router.replace({
    query: {
      ...route.query,
      userId: user ?? undefined,
      jobId: job ?? undefined, 
    }, 
  })
})

const pairParams = computed(() => ({
  userId: userId.value ?? 0,
  jobId: jobId.value ?? 0, 
}))
const { data, isPending, isError } = useGetAdminStandInPair(pairParams, {
  query: { enabled: computed(() => !isProd && userId.value !== null && jobId.value !== null) },
})

const sides: {
  side: StandInSide,
  title: string 
}[] = [
  {
    side: 'User',
    title: 'Namens kandidaat', 
  },
  {
    side: 'Employer',
    title: 'Namens werkgever', 
  },
  {
    side: 'Time',
    title: 'Tijd en reset', 
  },
]

const actionsBySide = computed(() => {
  const actions = data.value?.actions ?? []

  return sides.map(s => ({
    ...s,
    actions: actions.filter(a => a.side === s.side), 
  }))
})

// Logs in to the portal as the Job's Company, straight on the Match or else the JobApplication
const companyStore = useCompanyStore()
const portalPath = computed(() => {
  const pair = data.value?.pair
  if (pair?.match) {
    return `/matches/${pair.match.matchId}`
  }

  return pair?.jobApplication ? `/job-applications/${pair.jobApplication.jobApplicationId}` : null
})

async function openInPortal() {
  if (!data.value || !portalPath.value) {
    return
  }
  await companyStore.forwardToPortal(data.value.pair.job.companyId, portalPath.value)
}

const { mutateAsync, isPending: isPerforming, variables } = usePerformAdminStandInAction()

function isPendingFor(key: StandInActionKey) {
  return isPerforming.value && variables.value?.data?.actionKey === key
}

async function perform(action: StandInActionDto, args: Record<string, string>) {
  if (userId.value === null || jobId.value === null) {
    return
  }

  try {
    const result = await mutateAsync({
      data: {
        userId: userId.value,
        jobId: jobId.value,
        actionKey: action.key,
        arguments: args, 
      },
    })

    toast.success(action.label, result.message)
  } finally {
    // A failed action can still have changed state (e.g. a MatchScore calculated before a refused like).
    await queryClient.invalidateQueries({ queryKey: getGetAdminStandInPairQueryKey(pairParams) })
  }
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <Card>
      <template #content>
        <div class="mb-4 max-w-3xl">
          <h3 class="mb-2">
            Stand-in acties
          </h3>
          <p class="m-0 text-sm text-surface-600 leading-relaxed">
            Voer echte acties uit namens een kandidaat of werkgever en bekijk het resultaat in de app of het portaal.
            Alle gevolgen (pushberichten, mails, realtime updates) gebeuren echt.
          </p>
        </div>

        <Message v-if="isProd" severity="warn">
          Stand-in acties zijn niet beschikbaar op productie.
        </Message>
        <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StandInCandidatePicker v-model="userId" :selected-label="data?.pair.user.displayName" />
          <StandInJobPicker v-model="jobId" :selected-label="data?.pair.job.title" />
        </div>
      </template>
    </Card>

    <template v-if="!isProd && userId !== null && jobId !== null">
      <Card v-if="isPending">
        <template #content>
          <Skeleton height="12rem" />
        </template>
      </Card>
      <Message v-else-if="isError || !data" severity="error">
        Kandidaat of vacature niet gevonden.
      </Message>
      <div v-else class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Card>
          <template #title>
            <div class="flex items-start justify-between gap-2">
              <span>{{ data.pair.user.displayName }} × {{ data.pair.job.title }}</span>
              <Button
                v-if="portalPath"
                size="small"
                outlined
                icon="fa-regular fa-arrow-up-right-from-square"
                :label="data.pair.match ? 'Match in portaal' : 'Sollicitatie in portaal'"
                class="shrink-0"
                @click="openInPortal"
              />
            </div>
          </template>
          <template #subtitle>
            {{ data.pair.job.companyName }}
          </template>
          <template #content>
            <StandInPairState :pair="data.pair" />
          </template>
        </Card>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card v-for="group in actionsBySide" :key="group.side">
            <template #title>
              {{ group.title }}
            </template>
            <template #content>
              <div class="flex flex-col gap-3">
                <StandInActionCard
                  v-for="action in group.actions"
                  :key="action.key"
                  :action="action"
                  :is-pending="isPendingFor(action.key)"
                  @perform="perform(action, $event)"
                />
              </div>
            </template>
          </Card>
        </div>
      </div>
    </template>
  </div>
</template>
